import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * COMPONENT: GlobalLoadingOverlay
 *
 * Provides a full-screen loading indicator for critical operations:
 * - Prevents user interaction during loading
 * - Shows progress message
 * - Timeout warning after extended loading
 *
 * Fixes: UI freezing, unclear loading states, user confusion during long operations
 */

interface GlobalLoadingOverlayProps {
  isLoading: boolean;
  message?: string;
  subMessage?: string;
  progress?: number;
  showTimeout?: boolean;
  onCancel?: () => void;
}

export const GlobalLoadingOverlay: React.FC<GlobalLoadingOverlayProps> = ({
  isLoading,
  message = 'Loading...',
  subMessage,
  progress,
  showTimeout = false,
  onCancel,
}) => {
  if (!isLoading) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-gray-900/80 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
        {/* Spinner */}
        <div className="flex justify-center mb-6">
          <Loader2 className="h-12 w-12 text-blue-400 animate-spin" />
        </div>

        {/* Message */}
        <div className="text-center">
          <h3 className="text-lg font-semibold text-white mb-2">{message}</h3>
          {subMessage && (
            <p className="text-sm text-gray-400 mb-4">{subMessage}</p>
          )}

          {/* Progress Bar */}
          {progress !== undefined && (
            <div className="w-full bg-gray-700 rounded-full h-2 mb-4">
              <div
                className="bg-blue-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>
          )}

          {/* Timeout Warning */}
          {showTimeout && (
            <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-800 rounded text-yellow-400 text-xs">
              <p className="font-medium mb-1">This is taking longer than expected</p>
              <p className="text-yellow-300/80">
                Please check your internet connection. The operation will continue automatically.
              </p>
            </div>
          )}

          {/* Cancel Button */}
          {onCancel && (
            <button
              onClick={onCancel}
              className="mt-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
